import { useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Grid } from './EngineViewport';

/**
 * AutoCAD-parity adaptive drafting grid (GRIDADAPTIVE + GRIDMAJOR).
 *
 * The drei Grid draws two fixed line families (cellSize + sectionSize), so a
 * static configuration is only legible in one zoom band: zoomed out, the fine
 * grid aliases into a dense mesh that reads as a second grid stacked on the
 * section grid; zoomed in, the section lines leave the viewport entirely.
 *
 * This shell keeps the same two-tier drawing but adapts the spacing to the
 * camera on every rendered frame (the demand loop renders on zoom/pan, so the
 * uniforms refresh exactly when the view changes — zero idle cost):
 *
 * - Minor spacing is the smallest value on the classic 1-2-5 ladder whose
 *   on-screen spacing stays above MIN_CELL_PX, so the grid always reads as a
 *   single uniform AutoCAD-style screen grid across the whole zoom range.
 * - Major lines every GRIDMAJOR minors (AutoCAD GRIDMAJOR default = 5).
 * - followCamera keeps the infinite plane centered under the view, and
 *   fadeStrength = 0 flattens drei's radial alpha fade, so the grid covers
 *   the full viewport uniformly at every pan/zoom (fadeDistance only sizes
 *   the drawn plane — AutoCAD's grid does not dim toward the edges).
 */

/** Smallest acceptable on-screen spacing of the minor grid, in CSS px. */
const MIN_CELL_PX = 12;
/** Major lines are drawn every Nth minor line (AutoCAD GRIDMAJOR default). */
const GRIDMAJOR = 5;

/** World units spanned by one CSS pixel at the XY drafting plane (Z-up). */
function worldPerPixel(camera: THREE.Camera, viewportHeight: number): number {
    if (camera instanceof THREE.OrthographicCamera) {
        // Ortho plan camera: zoom maps world units to CSS pixels 1:1.
        return 1 / camera.zoom;
    }
    const { fov, near, position } = camera as THREE.PerspectiveCamera;
    const dist = Math.abs(position.z);
    if (dist <= near) return Number.POSITIVE_INFINITY;
    return (2 * dist * Math.tan((fov * Math.PI) / 360)) / viewportHeight;
}

/** Smallest value >= x on the 1-2-5 decade ladder (... 0.5 1 2 5 10 ...). */
function nextLadder(x: number): number {
    const clamped = Math.min(Math.max(x, 1e-6), 1e6);
    const base = Math.pow(10, Math.floor(Math.log10(clamped)));
    for (const m of [1, 2, 5]) {
        if (m * base >= clamped - base * 1e-6) return m * base;
    }
    return 10 * base;
}

export function AdaptiveCadGrid({
    cellColor,
    sectionColor,
}: {
    cellColor: string;
    sectionColor: string;
}) {
    const camera = useThree((s) => s.camera);
    const size = useThree((s) => s.size);
    const gridRef = useRef<THREE.Mesh | null>(null);

    useFrame(() => {
        const material = gridRef.current?.material as THREE.ShaderMaterial | undefined;
        const uniforms = material?.uniforms;
        if (!uniforms) return;
        const wpp = worldPerPixel(camera, size.height);
        if (!Number.isFinite(wpp) || wpp <= 0) return;
        const minor = nextLadder(MIN_CELL_PX * wpp);
        uniforms.cellSize.value = minor;
        uniforms.sectionSize.value = minor * GRIDMAJOR;
        // Size the drawn plane from the viewport half-diagonal (with margin):
        // the fade is flat (fadeStrength 0), so this only bounds the mesh.
        uniforms.fadeDistance.value = 3 * (Math.hypot(size.width, size.height) / 2) * wpp;
    });

    return (
        <Grid
            ref={gridRef}
            args={[100, 100]}
            cellSize={1}
            cellThickness={0.5}
            cellColor={cellColor}
            sectionSize={GRIDMAJOR}
            sectionThickness={1}
            sectionColor={sectionColor}
            fadeDistance={100}
            fadeStrength={0}
            infiniteGrid
            followCamera
            position={[0, 0, 0]}
            rotation={[Math.PI / 2, 0, 0]}
        />
    );
}
